"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Gift, Heart, Star, Sparkles, Crown } from "lucide-react";

interface AlertData {
  type: "subscribe" | "follow" | "subgift" | "cheer" | null;
  username: string;
  recipient?: string;
  amount?: number;
  tier?: string;
  months?: number;
}

export default function DynamicIsland({
  alert,
  isVisible,
}: {
  alert: AlertData;
  isVisible: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setTimeout(() => setExpanded(true), 900); // Wait 0.1 seconds before expanding
    } else {
      setExpanded(false);
    }
  }, [isVisible]);

  // Get the appropriate icon based on alert type
  const getIcon = () => {
    switch (alert.type) {
      case "subscribe":
        return <Crown className="h-5 w-5 text-yellow-400" />;
      case "follow":
        return <Heart className="h-5 w-5 text-red-500" />;
      case "subgift":
        return <Gift className="h-5 w-5 text-purple-500" />;
      case "cheer":
        return <Sparkles className="h-5 w-5 text-blue-400" />;
      default:
        return <Star className="h-5 w-5" />;
    }
  };

  // Get the appropriate text content based on alert type
  const getContent = () => {
    switch (alert.type) {
      case "subscribe":
        return (
          <div className="flex flex-col">
            <span className="font-bold">{alert.username}</span>
            <span className="text-xs opacity-80">
              {alert.months && alert.months > 1
                ? `${alert.months} months ${alert.tier} sub!`
                : `New ${alert.tier} sub!`}
            </span>
          </div>
        );
      case "follow":
        return (
          <div className="flex flex-col">
            <span className="font-bold">{alert.username}</span>
            <span className="text-xs opacity-80">New follow!</span>
          </div>
        );
      case "subgift":
        return (
          <div className="flex flex-col">
            <span className="font-bold">{alert.username}</span>
            <span className="text-xs opacity-80">{alert.recipient}!</span>
          </div>
        );
      case "cheer":
        return (
          <div className="flex flex-col">
            <span className="font-bold">{alert.username}</span>
            <span className="text-xs opacity-80">
              Tipped {alert.amount} bits!
            </span>
          </div>
        );
      default:
        return <span>Alert!</span>;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 1, y: -100, width: 45, height: 45 }}
      animate={{
        opacity: isVisible ? 1 : 0,
        y: isVisible ? 0 : -20,
        width: expanded ? 256 : 45,
        height: expanded ? 64 : 45,
      }}
      transition={{ duration: 0.8, ease: [0.68, -0.2, 0.27, 1.55] }}
      className="bg-black text-white rounded-full overflow-hidden shadow-lg shadow-black/30 flex items-center"
    >
      <div className="h-full flex items-center justify-start gap-4">
        <div
          className={`flex-shrink-0 flex items-center justify-center bg-black rounded-full transition-all duration-600 ${
            expanded ? "pl-4" : "pl-3"
          }`}
        >
          {getIcon()}
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: expanded ? 1 : 0 }}
          transition={{
            duration: 0.5,
            ease: [0.68, -0.2, 0.27, 1.55],
            delay: 0.2,
          }}
          className="flex-grow overflow-hidden"
        >
          {expanded && getContent()}
        </motion.div>
      </div>
    </motion.div>
  );
}
